import { Link, NavLink } from 'react-router-dom';
import { allDirectors, allGenres, getMoviesByIds, specials } from '@/lib/data';
import { movies } from '@/mocks/movies';

interface SidePanelProps {
  onNavigate?: () => void;
}

export default function SidePanel({ onNavigate }: SidePanelProps) {
  const topRated = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 5);

  return (
    <aside className="flex flex-col gap-6">
      {/* 分类筛选 */}
      <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
          <i className="ri-price-tag-3-line text-primary-500" aria-hidden="true" />
          电影分类
        </h3>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {allGenres.map((g) => (
            <Link
              key={g}
              to={`/category?genre=${encodeURIComponent(g)}`}
              onClick={onNavigate}
              className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
            >
              {g}
            </Link>
          ))}
        </div>

        <h3 className="mt-6 flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
          <i className="ri-user-star-line text-primary-500" aria-hidden="true" />
          导演
        </h3>
        <ul className="mt-3 space-y-1.5">
          {allDirectors.slice(0, 6).map((d) => (
            <li key={d}>
              <Link
                to={`/category?director=${encodeURIComponent(d)}`}
                onClick={onNavigate}
                className="flex items-center justify-between text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <span>{d}</span>
                <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* 专题入口 */}
      <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
          <i className="ri-book-marked-line text-accent-600" aria-hidden="true" />
          影评专题
        </h3>
        <ul className="mt-4 space-y-3">
          {specials.map((s) => (
            <li key={s.id}>
              <Link
                to="/special"
                onClick={onNavigate}
                className="group block cursor-pointer"
              >
                <span className="block text-sm font-medium text-foreground-800 group-hover:text-accent-600 transition-colors line-clamp-1">
                  {s.title}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-500 line-clamp-1">{s.subtitle}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* 高分推荐 */}
      <div className="rounded-lg border border-background-200/80 bg-primary-50/60 p-5">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
          <i className="ri-fire-line text-accent-500" aria-hidden="true" />
          高分推荐
        </h3>
        <ul className="mt-4 space-y-3">
          {topRated.map((m, i) => (
            <li key={m.id}>
              <Link
                to={`/detail/${m.id}`}
                onClick={onNavigate}
                className="flex items-center gap-3 group cursor-pointer"
              >
                <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-md bg-primary-500 font-label text-xs font-bold text-background-50">
                  {i + 1}
                </span>
                <span className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img src={m.cover} alt={`${m.title} 封面`} title={m.title} className="w-full h-full object-cover object-top" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {m.title}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                    <span className="font-label font-semibold">{m.rating.toFixed(1)}</span>
                    <span>· {m.year}</span>
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* 快捷入口 */}
      <div className="rounded-lg border border-background-200/80 bg-background-50 p-3">
        <NavLink
          to="/category"
          onClick={onNavigate}
          className="flex items-center justify-center gap-2 h-10 rounded-md bg-primary-500 font-label text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-layout-grid-line" aria-hidden="true" />
          浏览全部电影
        </NavLink>
        <p className="mt-2 text-center text-[11px] text-foreground-400">
          共收录 {getMoviesByIds(movies.map((m) => m.id)).length} 部影片
        </p>
      </div>
    </aside>
  );
}